<template>
  <v-card
    height="600px"
    class="d-flex flex-column"
  >
    <v-card-title class="d-flex justify-space-between align-center">
      <span class="text-h5">{{
        $t("admin.productsDatabase.publishersDatabase.title")
      }}</span>
      <v-btn
        icon
        @click="$emit('close')"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="flex-grow-0">
      <p>{{ $t("admin.productsDatabase.publishersDatabase.description") }}</p>

      <v-row>
        <v-col
          cols="12"
          md="8"
        >
          <v-text-field
            v-model="searchQuery"
            :label="
              $t('admin.productsDatabase.publishersDatabase.searchPlaceholder')
            "
            prepend-icon="mdi-magnify"
            outlined
            dense
            @input="debounceSearch"
          />
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text class="flex-grow-1 overflow-y-auto">
      <ekitabu-data-table
        :headers="headers"
        :items="publishers"
        :loading="loading"
        :items-per-page="-1"
        hide-default-footer
      >
        <!-- Description column with truncation -->
        <template #item.description="{ item }">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <div
                v-bind="attrs"
                class="text-truncate"
                style="max-width: 300px"
                v-on="on"
              >
                {{
                  item.description ||
                    $t("admin.productsDatabase.publishersDatabase.noDescription")
                }}
              </div>
            </template>
            <span>{{
              item.description ||
                $t("admin.productsDatabase.publishersDatabase.noDescription")
            }}</span>
          </v-tooltip>
        </template>

        <!-- Date Added column -->
        <template #item.createdAt="{ item }">
          {{ new Date(item.createdAt).toLocaleDateString() }}
        </template>

        <!-- Action column -->
        <template #item.action="{ item }">
          <v-btn
            small
            icon
            @click="openEditDialog(item)"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            color="error"
            small
            icon
            @click="confirmDeletePublisher(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </ekitabu-data-table>

      <!-- Infinite scroll loading indicator -->
      <div
        v-if="loading && publishers.length > 0"
        class="text-center my-2"
      >
        <v-progress-circular indeterminate />
      </div>

      <!-- Intersection observer target -->
      <div ref="loadMoreTrigger" />
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-4">
      <v-btn
        color="primary"
        large
        rounded
        @click="openCreateDialog"
      >
        {{ $t("admin.productsDatabase.publishersDatabase.addPublisher") }}
      </v-btn>
    </v-card-actions>

    <!-- Delete confirmation dialog -->
    <v-dialog
      v-model="deleteDialog"
      max-width="400px"
    >
      <v-card>
        <v-card-title>
          {{
            $t("admin.productsDatabase.publishersDatabase.confirmDelete")
          }}
        </v-card-title>
        <v-card-text>
          {{
            $t("admin.productsDatabase.publishersDatabase.confirmDeleteText", {
              name: publisherToDelete?.name,
            })
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            @click="deleteDialog = false"
          >
            {{ $t("common.cancel") }}
          </v-btn>
          <v-btn
            color="red darken-1"
            text
            @click="deletePublisher"
          >
            {{ $t("common.remove") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <PublisherProfileDialog
      v-model="profileDialog"
      :publisher="editingPublisher"
      :uploading-image="uploadingImage"
      :upload-progress="uploadProgress"
      @save="handleSave"
    />
  </v-card>
</template>

<script>
import axios from "axios";
import PublisherQueries from "@/graphql/PublisherQueries";
import infiniteScrollMixin from "@/mixins/infiniteScrollMixin";
import PublisherProfileDialog from "@/views/admin/ProductsDatabase/PublisherProfileDialog.vue";
import { removeIriId } from "@/iris";
import { AUTH_TOKEN } from "@/apollo/vue-apollo";

const apiBaseUrl = process.env.VUE_APP_API_HOST || "https://localhost";

export default {
  name: "PublishersDatabase",
  components: { PublisherProfileDialog },
  mixins: [infiniteScrollMixin],
  emits: ["close"],

  data() {
    return {
      headers: [
        { text: this.$t("common.name"), value: "name" },
        {
          text: this.$t(
            "admin.productsDatabase.publishersDatabase.descriptionHeader"
          ),
          value: "description",
        },
        {
          text: this.$t(
            "admin.productsDatabase.publishersDatabase.dateAddedHeader"
          ),
          value: "createdAt",
        },
        { text: "", value: "action", sortable: false, width: "100px" },
      ],
      deleteDialog: false,
      publisherToDelete: null,
      profileDialog: false,
      editingPublisher: null,
      uploadingImage: false,
      uploadProgress: 0,
    };
  },

  computed: {
    publishers() {
      return this.items;
    },
  },

  methods: {
    async fetchItems() {
      this.loading = true;
      try {
        const result = await this.$apollo.query({
          query: PublisherQueries.getPublishers,
          variables: {
            first: this.fetchSize,
            after: this.pageInfo.endCursor,
            name: this.searchQuery || null,
          },
          fetchPolicy: "network-only",
        });

        const { edges, pageInfo, totalCount } = result.data.publishers;

        if (edges && edges.length > 0) {
          this.items = [...this.items, ...edges.map((edge) => edge.node)];
        }

        // Update page info
        this.pageInfo = {
          hasNextPage: pageInfo.hasNextPage,
          endCursor: pageInfo.endCursor,
        };
      } catch (error) {
        console.error("Error fetching publishers:", error);
      } finally {
        this.loading = false;
      }
    },

    backendToForm(publisher) {
      return {
        id: publisher.id,
        name: publisher.name || "",
        description: publisher.description || "",
        website: publisher.websiteUrl || "",
        youtube: publisher.youtubeHandle || "",
        facebook: publisher.facebookHandle || "",
        instagram: publisher.instagramHandle || "",
        tiktok: publisher.tiktokHandle || "",
        x: publisher.twitterHandle || "",
        imageUrl: publisher.imageUrl || "",
      };
    },

    formToBackend(payload) {
      const nullIfEmpty = (v) => (v && v.length ? v : null);
      return {
        name: payload.name,
        description: payload.description,
        websiteUrl: nullIfEmpty(payload.website),
        twitterHandle: nullIfEmpty(payload.x),
        instagramHandle: nullIfEmpty(payload.instagram),
        tiktokHandle: nullIfEmpty(payload.tiktok),
        facebookHandle: nullIfEmpty(payload.facebook),
        youtubeHandle: nullIfEmpty(payload.youtube),
      };
    },

    openCreateDialog() {
      this.editingPublisher = null;
      this.profileDialog = true;
    },

    openEditDialog(publisher) {
      this.editingPublisher = this.backendToForm(publisher);
      this.profileDialog = true;
    },

    async handleSave({ payload, mode }) {
      if (mode === "create") {
        await this.createPublisher(payload);
      } else {
        await this.updatePublisher(payload);
      }
    },

    async createPublisher(payload) {
      this.loading = true;
      let imageUploadFailed = false;
      try {
        const result = await this.$apollo.mutate({
          mutation: PublisherQueries.addPublisher,
          variables: this.formToBackend(payload),
        });

        let newPublisher = result.data.createPublisher.publisher;

        if (payload.profileImageFile) {
          this.uploadingImage = true;
          this.uploadProgress = 0;
          try {
            const uploadResult = await this.uploadPublisherImage(
              newPublisher._id,
              payload.profileImageFile
            );
            newPublisher = {
              ...newPublisher,
              imageUrl: uploadResult.mediaObject.filePath,
            };
          } catch (uploadError) {
            console.error("Error uploading publisher image:", uploadError);
            imageUploadFailed = true;
          } finally {
            this.uploadingImage = false;
            this.uploadProgress = 0;
          }
        }

        this.items = [newPublisher, ...this.items];
        this.profileDialog = false;

        if (imageUploadFailed) {
          this.showNotification(
            this.$t(
              "admin.productsDatabase.publishersDatabase.publisherAddedImageFailed"
            ),
            "warning"
          );
        } else {
          this.showNotification(
            this.$t("admin.productsDatabase.publishersDatabase.publisherAdded")
          );
        }
      } catch (error) {
        console.error("Error adding publisher:", error);
        this.showNotification(
          error.message || this.$t("errors.general"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },

    async updatePublisher(payload) {
      if (!this.editingPublisher || !this.editingPublisher.id) return;

      this.loading = true;
      let imageUploadFailed = false;
      try {
        const publisherUuid = removeIriId(this.editingPublisher.id);

        const result = await this.$apollo.mutate({
          mutation: PublisherQueries.updatePublisher,
          variables: {
            id: this.editingPublisher.id,
            ...this.formToBackend(payload),
          },
        });

        let updated = result.data.updatePublisher.publisher;

        if (payload.profileImageFile) {
          this.uploadingImage = true;
          this.uploadProgress = 0;
          try {
            const uploadResult = await this.uploadPublisherImage(
              publisherUuid,
              payload.profileImageFile
            );
            updated = {
              ...updated,
              imageUrl: uploadResult.mediaObject.filePath,
            };
          } catch (uploadError) {
            console.error("Error uploading publisher image:", uploadError);
            imageUploadFailed = true;
          } finally {
            this.uploadingImage = false;
            this.uploadProgress = 0;
          }
        }

        this.items = this.items.map((p) =>
          p.id === updated.id ? updated : p
        );
        this.profileDialog = false;

        if (imageUploadFailed) {
          this.showNotification(
            this.$t(
              "admin.productsDatabase.publishersDatabase.publisherUpdatedImageFailed"
            ),
            "warning"
          );
        } else {
          this.showNotification(
            this.$t("admin.productsDatabase.publishersDatabase.publisherUpdated")
          );
        }
      } catch (error) {
        console.error("Error updating publisher:", error);
        this.showNotification(
          error.message || this.$t("errors.general"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },

    async uploadPublisherImage(publisherUuid, file) {
      const formData = new FormData();
      formData.append("file", file);

      const token = localStorage.getItem(AUTH_TOKEN);
      const headers = {
        "Content-Type": "multipart/form-data",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.post(
        `${apiBaseUrl}/publishers/${publisherUuid}/image`,
        formData,
        {
          headers,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              this.uploadProgress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            }
          },
        }
      );

      return response.data;
    },

    confirmDeletePublisher(publisher) {
      this.publisherToDelete = publisher;
      this.deleteDialog = true;
    },

    async deletePublisher() {
      if (!this.publisherToDelete) return;

      this.loading = true;
      try {
        await this.$apollo.mutate({
          mutation: PublisherQueries.deletePublisher,
          variables: {
            input: { id: this.publisherToDelete.id },
          },
        });

        // Remove the publisher from the list
        this.items = this.items.filter(
          (publisher) => publisher.id !== this.publisherToDelete.id
        );

        // Close the dialog
        this.deleteDialog = false;
        this.publisherToDelete = null;

        // Show success notification
        this.showNotification(
          this.$t("admin.productsDatabase.publishersDatabase.publisherDeleted")
        );
      } catch (error) {
        console.error("Error deleting publisher:", error);
        this.showNotification(
          error.message || this.$t("errors.general"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-y: visible;
}
</style>
